import { Button, Text } from '@msc-b2c/shared-components';
import { CartBodyLabelsProps, CartItem, OBSObjI } from '@msc-b2c/interfaces/types';
import { CardInfo } from './CardInfo';
import styles from './carddetails.module.scss';
import { MyMscExcursion } from '@msc-b2c/interfaces/interfaces';
import { managePlurals, replacePlaceholders, RootState } from '@msc-b2c/shared-utils';
import { OBSTypesEnum, PriceBasisEnum } from '@msc-b2c/interfaces/enums';
import { useSelector } from 'react-redux';
import { getDiningReservationRows } from './cardDetailsDining.utils';

export function CardDetails({
  item,
  labels,
  service,
  passengers,
  title,
  onEdit,
  onDiningEdit,
  onDelete,
}: Readonly<{
  item: CartItem;
  labels: Readonly<CartBodyLabelsProps>;
  service: OBSObjI | MyMscExcursion;
  passengers: string[];
  /** Merged title for combined cards; falls back to the service description. */
  title?: string;
  onEdit?: () => void;
  onDiningEdit?: () => void;
  onDelete?: (itemCode: string, itemDescription: string, startDate?: string) => void;
}>) {
  const itinerary = useSelector((state: RootState) => state.myCruise?.itinerary ?? null);

  const specialityRestaurantsFromCf = useSelector(
    (state: RootState) => state.myAreaCommonConfig.specialtyRestaurants
  );

  const diningReservationRows = getDiningReservationRows(
    item,
    itinerary,
    {
      reservationLabel: labels.reservationFieldText,
      dayLabel: labels.dayLabelText,
      dayAtSeaLabel: labels.dayAtSeaLabelText,
    },
    specialityRestaurantsFromCf
  );

  const handleRemoveItem = () => {
    if (!service.code || !onDelete) return;
    onDelete(service.code, service.description || service.code, item.startDate);
  };

  const handleEditItem = () => {
    if ('type' in service && service.type?.code === OBSTypesEnum.RESTAURANTS && onDiningEdit) {
      onDiningEdit();
      return;
    }

    onEdit?.();
  };

  return (
    <div className={styles['cmp-carddetails']}>
      <div className={styles['cmp-carddetails__text']}>
        <Text
          text={title || service.description}
          styleOption={`${styles['cmp-carddetails__title']} body-big bold`}
          visible={true}
        />

        <CardInfo
          label={managePlurals(passengers.length, labels.passengersFieldText)}
          items={[...passengers]}
        />

        {diningReservationRows && (
          <CardInfo
            label={diningReservationRows.label}
            items={diningReservationRows.rows}
            allowWrap={true}
          />
        )}

        {'duration' in service &&
        service.duration &&
        labels.durationFieldText &&
        labels.durationValueLabel ? (
          <CardInfo
            label={labels.durationFieldText}
            items={[
              managePlurals(
                service.duration,
                replacePlaceholders(labels.durationValueLabel, {
                  duration: service.duration.toString(),
                })
              ),
            ]}
          />
        ) : null}

        <div className={styles['cmp-carddetails__actions']}>
          {onEdit && (
            <Button
              text={labels.editButtonText}
              styleOption={`${styles['cmp-carddetails__actions-action']} cmp-button__link body-small`}
              onClick={handleEditItem}
              disabled={
                service.priceBasis === PriceBasisEnum.PER_CABIN ||
                ('type' in service && service.type.code === 'ALLINC') ||
                ('prepaidItems' in service && service.prepaidItems?.isAllInclusive) ||
                Boolean(
                  item.timeslotsSelectionNotAvailable ||
                    item.diningSelection?.timeslotsSelectionNotAvailable
                )
              }
            />
          )}
          {onDelete && (
            <Button
              text={labels.removeButtonText}
              styleOption={`${styles['cmp-carddetails__actions-action']} cmp-button__link body-small`}
              onClick={handleRemoveItem}
            />
          )}
        </div>
      </div>
    </div>
  );
}
